/**
 * Quran Audio Player
 * A Spotify-like audio player for Quran recitations
 */

class QuranAudioPlayer {
  constructor(container) {
    this.container = container;
    this.audio = new Audio();
    this.tracks = [];
    this.currentTrackIndex = -1;
    this.isPlaying = false;
    this.isShuffle = false;
    this.isRepeat = false;
    this.isMuted = false;
    this.volumeBeforeMute = 1;
    this.originalTrackOrder = [];
    this.isDragging = false;
    
    // Swipe gesture properties
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeCurrentX = 0;
    this.isSwiping = false;
    
    this.init();
  }

  init() {
    this.setupTracks();
    this.setupEventListeners();
    this.setupAudioEvents();
    this.setupSwipeGestures();
    
    // Initialize player visibility
    const playerElement = this.container.querySelector('.quran-audio-player');
    if (playerElement && this.tracks.length > 0) {
      playerElement.style.display = 'none';
    }
  }

  setupTracks() {
    const trackRows = this.container.querySelectorAll('.qa-track-row');
    trackRows.forEach((row, index) => {
      const track = {
        index: index,
        element: row,
        title: row.dataset.title || '',
        artist: row.dataset.artist || '',
        audioUrl: row.dataset.audio || '',
        duration: row.dataset.duration || '0:00',
        number: row.dataset.number || (index + 1),
        durationSeconds: 0
      };
      this.tracks.push(track);
      
      // Load audio metadata to get actual duration
      if (track.audioUrl) {
        this.loadTrackDuration(track, row);
      }
    });
    
    this.originalTrackOrder = [...this.tracks];
  }

  loadTrackDuration(track, row) {
    const audio = new Audio();
    audio.preload = 'metadata';
    
    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        track.durationSeconds = audio.duration;
        track.duration = this.formatTime(audio.duration);
        
        // Update the duration in the table
        const durationCell = row.querySelector('.qa-track-duration');
        if (durationCell) {
          durationCell.textContent = track.duration;
        }
      }
    });
    
    audio.addEventListener('error', () => {
      // Keep the manual duration if audio fails to load
      console.warn('Could not load duration for:', track.title);
    });
    
    audio.src = track.audioUrl;
  }

  setupEventListeners() {
    // Track row clicks
    const trackRows = this.container.querySelectorAll('.qa-track-row');
    trackRows.forEach((row, index) => {
      row.addEventListener('click', () => {
        // Find the track by its audio URL to ensure we play the correct track
        const audioUrl = row.dataset.audio;
        const trackIndex = this.tracks.findIndex(t => t.audioUrl === audioUrl);
        if (trackIndex !== -1) {
          this.playTrack(trackIndex);
        }
      });
    });

    // Shuffle button (Other Recitations)
    const shuffleBtn = this.container.querySelector('.qa-shuffle-btn');
    if (shuffleBtn) {
      shuffleBtn.addEventListener('click', () => {
        if (this.tracks.length > 0) {
          // Enable shuffle if not already enabled
          if (!this.isShuffle) {
            this.toggleShuffle();
          }
          // Start playing from a random track
          const randomIndex = Math.floor(Math.random() * this.tracks.length);
          this.playTrack(randomIndex);
        }
      });
    }

    // Play all button
    const playAllBtn = this.container.querySelector('.qa-play-all-btn');
    if (playAllBtn) {
      playAllBtn.addEventListener('click', () => {
        if (this.tracks.length > 0) {
          this.playTrack(0);
        }
      });
    }

    // Player controls
    const playPauseBtn = this.container.querySelector('.qa-play-pause-btn');
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', () => {
        this.togglePlayPause();
      });
    }

    const prevBtn = this.container.querySelector('.qa-prev-btn');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.previousTrack();
      });
    }

    const nextBtn = this.container.querySelector('.qa-next-btn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.nextTrack();
      });
    }

    const repeatBtn = this.container.querySelector('.qa-repeat-btn');
    if (repeatBtn) {
      repeatBtn.addEventListener('click', () => {
        this.toggleRepeat();
      });
    }

    const shuffleBtnPlayer = this.container.querySelector('.qa-shuffle-btn-player');
    if (shuffleBtnPlayer) {
      shuffleBtnPlayer.addEventListener('click', () => {
        this.toggleShuffle();
      });
    }

    // Like button
    const likeBtn = this.container.querySelector('.qa-like-btn');
    if (likeBtn) {
      likeBtn.addEventListener('click', () => {
        likeBtn.classList.toggle('active');
      });
    }

    // Volume button
    const volumeBtn = this.container.querySelector('.qa-volume-btn');
    if (volumeBtn) {
      volumeBtn.addEventListener('click', () => {
        this.toggleMute();
      });
    }

    // Progress bar - Click and Drag
    const progressBar = this.container.querySelector('.qa-progress-bar-wrapper .qa-progress-bar');
    if (progressBar) {
      // Click to seek
      progressBar.addEventListener('click', (e) => {
        if (!this.isDragging) {
          this.seek(e);
        }
      });

      // Mouse drag events
      progressBar.addEventListener('mousedown', (e) => {
        this.startDrag(e);
      });

      document.addEventListener('mousemove', (e) => {
        if (this.isDragging) {
          this.onDrag(e);
        }
      });

      document.addEventListener('mouseup', () => {
        if (this.isDragging) {
          this.stopDrag();
        }
      });

      // Touch drag events for mobile
      progressBar.addEventListener('touchstart', (e) => {
        this.startDrag(e.touches[0]);
      });

      document.addEventListener('touchmove', (e) => {
        if (this.isDragging) {
          this.onDrag(e.touches[0]);
          e.preventDefault();
        }
      }, { passive: false });

      document.addEventListener('touchend', () => {
        if (this.isDragging) {
          this.stopDrag();
        }
      });

      // Show scrubber on hover
      progressBar.addEventListener('mouseenter', () => {
        progressBar.classList.add('hovering');
      });

      progressBar.addEventListener('mouseleave', () => {
        if (!this.isDragging) {
          progressBar.classList.remove('hovering');
        }
      });
    }
  }

  setupAudioEvents() {
    this.audio.addEventListener('timeupdate', () => {
      this.updateProgress();
    });

    this.audio.addEventListener('ended', () => {
      this.handleTrackEnd();
    });

    this.audio.addEventListener('loadedmetadata', () => {
      this.updateDuration();
    });

    this.audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      this.showError('Unable to play audio. Please try another track.');
    });
  }

  playTrack(index) {
    if (index < 0 || index >= this.tracks.length) return;

    const track = this.tracks[index];
    
    if (!track.audioUrl) {
      this.showError('No audio file available for this track.');
      return;
    }

    this.currentTrackIndex = index;
    this.audio.src = track.audioUrl;
    this.audio.play().catch(error => {
      console.error('Playback error:', error);
      this.showError('Unable to play audio. Please try again.');
    });

    this.isPlaying = true;
    this.updateUI();
    this.showPlayer();
  }

  togglePlayPause() {
    if (this.currentTrackIndex === -1 && this.tracks.length > 0) {
      this.playTrack(0);
      return;
    }

    if (this.isPlaying) {
      this.audio.pause();
      this.isPlaying = false;
    } else {
      this.audio.play().catch(error => {
        console.error('Playback error:', error);
      });
      this.isPlaying = true;
    }

    this.updateUI();
  }

  nextTrack() {
    if (this.tracks.length === 0) return;

    let nextIndex = this.currentTrackIndex + 1;
    if (nextIndex >= this.tracks.length) {
      nextIndex = 0;
    }

    this.playTrack(nextIndex);
  }

  previousTrack() {
    if (this.tracks.length === 0) return;

    // If more than 3 seconds played, restart current track
    if (this.audio.currentTime > 3) {
      this.audio.currentTime = 0;
      return;
    }

    let prevIndex = this.currentTrackIndex - 1;
    if (prevIndex < 0) {
      prevIndex = this.tracks.length - 1;
    }

    this.playTrack(prevIndex);
  }

  toggleShuffle() {
    this.isShuffle = !this.isShuffle;

    if (this.isShuffle) {
      // Shuffle tracks
      this.shuffleTracks();
    } else {
      // Restore original order
      this.tracks = [...this.originalTrackOrder];
    }

    this.updateUI();
  }

  toggleRepeat() {
    this.isRepeat = !this.isRepeat;
    this.audio.loop = this.isRepeat;
    this.updateUI();
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    
    if (this.isMuted) {
      // Save current volume and mute
      this.volumeBeforeMute = this.audio.volume;
      this.audio.volume = 0;
    } else {
      // Restore previous volume
      this.audio.volume = this.volumeBeforeMute;
    }
    
    this.updateUI();
  }

  shuffleTracks() {
    const currentTrack = this.tracks[this.currentTrackIndex];
    
    // Fisher-Yates shuffle
    for (let i = this.tracks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.tracks[i], this.tracks[j]] = [this.tracks[j], this.tracks[i]];
    }

    // Update current track index
    if (currentTrack) {
      this.currentTrackIndex = this.tracks.findIndex(t => t.audioUrl === currentTrack.audioUrl);
    }
  }

  seek(event) {
    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const seekTime = percent * this.audio.duration;

    if (!isNaN(seekTime) && seekTime >= 0 && seekTime <= this.audio.duration) {
      this.audio.currentTime = seekTime;
    }
  }

  startDrag(event) {
    this.isDragging = true;
    const progressBar = this.container.querySelector('.qa-progress-bar-wrapper .qa-progress-bar');
    if (progressBar) {
      progressBar.classList.add('dragging');
      this.updateSeekPosition(event);
    }
  }

  onDrag(event) {
    if (this.isDragging) {
      this.updateSeekPosition(event);
    }
  }

  stopDrag() {
    this.isDragging = false;
    const progressBar = this.container.querySelector('.qa-progress-bar-wrapper .qa-progress-bar');
    if (progressBar) {
      progressBar.classList.remove('dragging');
      progressBar.classList.remove('hovering');
    }
  }

  updateSeekPosition(event) {
    const progressBar = this.container.querySelector('.qa-progress-bar-wrapper .qa-progress-bar');
    if (!progressBar || !this.audio.duration) return;

    const rect = progressBar.getBoundingClientRect();
    let clientX = event.clientX;
    
    // Clamp the position within the progress bar
    if (clientX < rect.left) clientX = rect.left;
    if (clientX > rect.right) clientX = rect.right;

    const percent = (clientX - rect.left) / rect.width;
    const seekTime = percent * this.audio.duration;

    if (!isNaN(seekTime) && seekTime >= 0 && seekTime <= this.audio.duration) {
      this.audio.currentTime = seekTime;
      this.updateProgress(); // Immediately update the UI
    }
  }

  handleTrackEnd() {
    if (!this.isRepeat) {
      this.nextTrack();
    }
  }

  updateProgress() {
    const progressFill = this.container.querySelector('.qa-progress-fill');
    const currentTimeEl = this.container.querySelector('.qa-time.current');

    if (this.audio.duration) {
      const percent = (this.audio.currentTime / this.audio.duration) * 100;
      if (progressFill) {
        progressFill.style.width = percent + '%';
      }
    }

    if (currentTimeEl) {
      currentTimeEl.textContent = this.formatTime(this.audio.currentTime);
    }
  }

  updateDuration() {
    const durationEl = this.container.querySelector('.qa-time.duration');
    if (durationEl && this.audio.duration) {
      durationEl.textContent = this.formatTime(this.audio.duration);
    }
  }

  updateUI() {
    // Update track rows
    const trackRows = this.container.querySelectorAll('.qa-track-row');
    trackRows.forEach((row, index) => {
      row.classList.toggle('active', index === this.currentTrackIndex);
    });

    // Update play/pause button
    const playPauseBtn = this.container.querySelector('.qa-play-pause-btn');
    if (playPauseBtn) {
      const icon = playPauseBtn.querySelector('svg use');
      if (icon) {
        icon.setAttribute('href', this.isPlaying ? '#icon-pause-large' : '#icon-play-large');
      }
    }

    // Update player info
    if (this.currentTrackIndex >= 0 && this.tracks[this.currentTrackIndex]) {
      const track = this.tracks[this.currentTrackIndex];
      
      const titleEl = this.container.querySelector('.qa-player-title');
      const artistEl = this.container.querySelector('.qa-player-artist');
      const thumbnailImg = this.container.querySelector('.qa-thumbnail-img');

      if (titleEl) titleEl.textContent = track.title;
      if (artistEl) artistEl.textContent = track.artist;
      
      // Update thumbnail with track number
      if (thumbnailImg) {
        const trackNumber = track.number || (this.currentTrackIndex + 1);
        thumbnailImg.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23ffffff' width='100' height='100'/%3E%3Ctext x='50' y='50' font-size='40' text-anchor='middle' dy='.3em' fill='%231db1bb' font-family='Arial'%3E${trackNumber}%3C/text%3E%3C/svg%3E`;
      }
    }

    // Update shuffle button
    const shuffleBtns = this.container.querySelectorAll('.qa-shuffle-btn, .qa-shuffle-btn-player');
    shuffleBtns.forEach(btn => {
      btn.style.color = this.isShuffle ? '#ffffff' : '';
    });

    // Update repeat button
    const repeatBtn = this.container.querySelector('.qa-repeat-btn');
    if (repeatBtn) {
      repeatBtn.style.color = this.isRepeat ? '#ffffff' : '';
    }

    // Update volume button
    const volumeBtn = this.container.querySelector('.qa-volume-btn');
    if (volumeBtn) {
      const volumeSvg = volumeBtn.querySelector('svg');
      if (volumeSvg) {
        // Update SVG path based on mute state
        if (this.isMuted) {
          volumeSvg.innerHTML = '<path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"></path>';
          volumeBtn.classList.add('muted');
        } else {
          volumeSvg.innerHTML = '<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"></path>';
          volumeBtn.classList.remove('muted');
        }
      }
    }
  }

  showPlayer() {
    const playerElement = this.container.querySelector('.quran-audio-player');
    if (playerElement) {
      playerElement.classList.add('active');
    }
  }

  formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  showError(message) {
    console.error(message);
    // You can implement a toast/notification system here
  }

  setupSwipeGestures() {
    const playerInfo = this.container.querySelector('.qa-player-info');
    if (!playerInfo) return;

    // Touch start
    playerInfo.addEventListener('touchstart', (e) => {
      this.swipeStartX = e.touches[0].clientX;
      this.swipeStartY = e.touches[0].clientY;
      this.swipeCurrentX = this.swipeStartX;
      this.isSwiping = false;
    }, { passive: true });

    // Touch move
    playerInfo.addEventListener('touchmove', (e) => {
      if (!this.swipeStartX) return;

      this.swipeCurrentX = e.touches[0].clientX;
      const swipeCurrentY = e.touches[0].clientY;
      
      const diffX = this.swipeCurrentX - this.swipeStartX;
      const diffY = swipeCurrentY - this.swipeStartY;
      
      // Only start swiping if horizontal movement is greater than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        this.isSwiping = true;
        
        // Apply transform to show swipe feedback
        playerInfo.style.transform = `translateX(${diffX * 0.5}px)`;
        playerInfo.style.transition = 'none';
        
        // Add visual feedback
        const opacity = 1 - (Math.abs(diffX) / 300);
        playerInfo.style.opacity = Math.max(0.5, opacity);
      }
    }, { passive: true });

    // Touch end
    playerInfo.addEventListener('touchend', (e) => {
      if (!this.isSwiping) {
        this.resetSwipe(playerInfo);
        return;
      }

      const diffX = this.swipeCurrentX - this.swipeStartX;
      const swipeThreshold = 80; // Minimum swipe distance

      // Reset with animation
      playerInfo.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      playerInfo.style.transform = 'translateX(0)';
      playerInfo.style.opacity = '1';

      // Swipe right → Previous track
      if (diffX > swipeThreshold) {
        setTimeout(() => {
          this.previousTrack();
        }, 100);
      }
      // Swipe left → Next track
      else if (diffX < -swipeThreshold) {
        setTimeout(() => {
          this.nextTrack();
        }, 100);
      }

      this.resetSwipe(playerInfo);
    }, { passive: true });

    // Touch cancel
    playerInfo.addEventListener('touchcancel', () => {
      this.resetSwipe(playerInfo);
    }, { passive: true });
  }

  resetSwipe(element) {
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeCurrentX = 0;
    this.isSwiping = false;
    
    if (element) {
      setTimeout(() => {
        element.style.transition = '';
        element.style.transform = '';
        element.style.opacity = '';
      }, 300);
    }
  }
}

// Initialize on Shopify theme editor load and page load
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  // For Shopify theme editor
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target;
    const container = section.querySelector('.quran-audio-section');
    if (container && !container.quranPlayer) {
      container.quranPlayer = new QuranAudioPlayer(container);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target;
    const container = section.querySelector('.quran-audio-section');
    if (container && container.quranPlayer) {
      if (container.quranPlayer.audio) {
        container.quranPlayer.audio.pause();
      }
      container.quranPlayer = null;
    }
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  const containers = document.querySelectorAll('.quran-audio-section');
  containers.forEach(container => {
    if (!container.quranPlayer) {
      container.quranPlayer = new QuranAudioPlayer(container);
    }
  });
});

